import { LitElement } from 'lit';

export class FangmoTabs extends LitElement {
  private initialized = false;
  // Use light DOM so that server-side rendered markup and styling remain intact
  // and accessible without breaking SSR or shadow boundary isolation.
  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this.initTabs();
  }

  private initTabs() {
    if (this.initialized) return;
    const tabList = this.querySelector<HTMLElement>('[role="tablist"]');
    if (!tabList) return;

    const tabs = Array.from(tabList.querySelectorAll<HTMLElement>('[role="tab"]'));
    const panels = Array.from(this.querySelectorAll<HTMLElement>('[role="tabpanel"]'));

    if (tabs.length === 0 || panels.length === 0) return;

    // Remove no-js default visibility classes/attributes
    this.initialized = true;
    this.classList.add('js-tabs-initialized');

    // Find initially active tab or default to first
    const activeTab = tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0];

    const activateTab = (tabToActivate: HTMLElement, moveFocus = true) => {
      for (const tab of tabs) {
        const isSelected = tab === tabToActivate;
        tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        tab.setAttribute('tabindex', isSelected ? '0' : '-1');
        if (isSelected) {
          tab.classList.add('active');
        } else {
          tab.classList.remove('active');
        }
      }

      const targetId = tabToActivate.getAttribute('aria-controls');
      for (const panel of panels) {
        if (panel.id === targetId) {
          panel.removeAttribute('hidden');
          panel.classList.remove('tabpanel-hidden');
        } else {
          panel.setAttribute('hidden', '');
          panel.classList.add('tabpanel-hidden');
        }
      }

      if (moveFocus) {
        tabToActivate.focus();
      }
    };

    // Bind click events
    for (const tab of tabs) {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        activateTab(tab, true);
      });
    }

    // Keyboard navigation: Arrows, Home, End
    tabList.addEventListener('keydown', (e: Event) => {
      const keyEvent = e as KeyboardEvent;
      const currentTab = document.activeElement as HTMLElement;
      const currentIndex = tabs.indexOf(currentTab);
      if (currentIndex === -1) return;

      let nextIndex: number;

      switch (keyEvent.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = (currentIndex + 1) % tabs.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      keyEvent.preventDefault();
      activateTab(tabs[nextIndex], true);
    });

    // Initial setup
    activateTab(activeTab, false);

    // Optional: search input inside panel
    const searchInputs = Array.from(this.querySelectorAll<HTMLInputElement>('[data-tab-filter]'));
    for (const input of searchInputs) {
      const targetSelector = input.getAttribute('data-tab-filter');
      if (!targetSelector) continue;
      const targetContainer = this.querySelector(targetSelector);
      if (!targetContainer) continue;
      const filterItems = Array.from(
        targetContainer.querySelectorAll<HTMLElement>('[data-filter-text]')
      );

      input.hidden = false;
      const empty = this.querySelector<HTMLElement>('[data-filter-empty]');
      input.addEventListener('input', () => {
        const query = input.value.trim().toLowerCase();
        let matches = 0;
        for (const item of filterItems) {
          const text = (
            item.getAttribute('data-filter-text') ||
            item.textContent ||
            ''
          ).toLowerCase();
          if (!query || text.includes(query)) {
            item.hidden = false;
            matches++;
          } else {
            item.hidden = true;
          }
        }
        if (empty) empty.hidden = matches > 0;
      });
    }
  }
}

if (!customElements.get('fangmo-tabs')) {
  customElements.define('fangmo-tabs', FangmoTabs);
}
